import Icon from "@imminently/decisively-ui/Icon";
import { Separator } from "@/ui/primitives/separator";
import { useTheme } from "@/app/providers/ThemeContext";
import { cn } from "@/lib/utils";

export const AppearancePanel = () => {
  const { theme, appearance, setTheme, setAppearance } = useTheme();

  return (
    <div className="p-8 space-y-8 max-w-2xl">
      <div>
        <h2 className="text-2xl font-bold">Appearance</h2>
        <p className="text-muted-foreground mt-1">Customize how the application looks</p>
      </div>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">Theme</h3>
        <div className="grid grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => setTheme("light")}
            className={cn(
              "relative flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all hover:border-primary/50",
              theme === "light" ? "border-primary" : "border-border",
            )}
          >
            <div className="w-full aspect-2/1 rounded-md border bg-white overflow-hidden">
              <div className="flex h-full">
                <div className="w-1/3 bg-gray-100 p-2 space-y-2">
                  <div className="w-8 h-8 rounded bg-primary" />
                  <div className="space-y-1">
                    <div className="h-3 w-full bg-primary/20 rounded" />
                    <div className="h-2 w-2/3 bg-gray-300 rounded" />
                  </div>
                </div>
                <div className="flex-1 bg-white" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="sun" className="w-4 h-4" />
              <span className="font-medium">Light</span>
            </div>
            {theme === "light" && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <Icon name="check" className="w-3 h-3 text-primary-foreground" />
              </div>
            )}
          </button>

          <button
            type="button"
            onClick={() => setTheme("dark")}
            className={cn(
              "relative flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all hover:border-primary/50",
              theme === "dark" ? "border-primary" : "border-border",
            )}
          >
            <div className="w-full aspect-2/1 rounded-md border border-gray-700 bg-gray-950 overflow-hidden">
              <div className="flex h-full">
                <div className="w-1/3 bg-gray-900 p-2 space-y-2">
                  <div className="w-8 h-8 rounded bg-primary" />
                  <div className="space-y-1">
                    <div className="h-3 w-full bg-primary/20 rounded" />
                    <div className="h-2 w-2/3 bg-gray-700 rounded" />
                  </div>
                </div>
                <div className="flex-1 bg-gray-950" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="moon" className="w-4 h-4" />
              <span className="font-medium">Dark</span>
            </div>
            {theme === "dark" && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <Icon name="check" className="w-3 h-3 text-primary-foreground" />
              </div>
            )}
          </button>

          <button
            type="button"
            onClick={() => setTheme("auto")}
            className={cn(
              "relative flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all hover:border-primary/50",
              theme === "auto" ? "border-primary" : "border-border",
            )}
          >
            <div className="w-full aspect-2/1 rounded-md overflow-hidden border">
              <div className="flex h-full">
                <div className="w-1/2 bg-white border-r">
                  <div className="flex h-full">
                    <div className="w-1/2 bg-gray-100 p-1.5 space-y-1">
                      <div className="w-6 h-6 rounded bg-primary" />
                    </div>
                    <div className="flex-1 bg-white" />
                  </div>
                </div>
                <div className="w-1/2 bg-gray-950">
                  <div className="flex h-full">
                    <div className="w-1/2 bg-gray-900 p-1.5 space-y-1">
                      <div className="w-6 h-6 rounded bg-primary" />
                    </div>
                    <div className="flex-1 bg-gray-950" />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="monitor" className="w-4 h-4" />
              <span className="font-medium">Auto</span>
            </div>
            {theme === "auto" && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <Icon name="check" className="w-3 h-3 text-primary-foreground" />
              </div>
            )}
          </button>
        </div>
      </section>

      <Separator />

      <section className="space-y-4">
        <div>
          <h3 className="text-base font-semibold">Sidebar Style</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Choose between classic design with dark sidebar or modern adaptive design
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setAppearance("classic")}
            className={cn(
              "relative flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all hover:border-primary/50",
              appearance === "classic" ? "border-primary" : "border-border",
            )}
          >
            <div className="w-full aspect-2/1 rounded-md border overflow-hidden bg-gray-100">
              <div className="flex h-full">
                <div className="w-1/3 bg-[#0A0A0A] p-2 space-y-2">
                  <div className="w-8 h-8 rounded bg-[#70F058]" />
                  <div className="space-y-1">
                    <div className="h-3 w-full bg-gray-700 rounded" />
                    <div className="h-2 w-2/3 bg-gray-700 rounded" />
                  </div>
                </div>
                <div className="flex-1 bg-white" />
              </div>
            </div>
            <span className="font-medium">Classic</span>
            {appearance === "classic" && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <Icon name="check" className="w-3 h-3 text-primary-foreground" />
              </div>
            )}
          </button>

          <button
            type="button"
            onClick={() => setAppearance("modern")}
            className={cn(
              "relative flex flex-col items-center gap-3 p-4 rounded-lg border-2 transition-all hover:border-primary/50",
              appearance === "modern" ? "border-primary" : "border-border",
            )}
          >
            <div className="w-full aspect-2/1 rounded-md border overflow-hidden bg-gray-100">
              <div className="flex h-full">
                <div className="w-1/3 bg-gray-50 p-2 space-y-2 border-r border-gray-200">
                  <div className="w-8 h-8 rounded bg-gray-900" />
                  <div className="space-y-1">
                    <div className="h-3 w-full bg-gray-200 rounded" />
                    <div className="h-2 w-2/3 bg-gray-300 rounded" />
                  </div>
                </div>
                <div className="flex-1 bg-white" />
              </div>
            </div>
            <span className="font-medium">Modern</span>
            {appearance === "modern" && (
              <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <Icon name="check" className="w-3 h-3 text-primary-foreground" />
              </div>
            )}
          </button>
        </div>
      </section>
    </div>
  );
};
